/**
 * Every chrome entry's EXISTING gate, as one pure function (#15618, #15689).
 *
 * This map lived inline in `horizontal-header.tsx` (#14279, #14657), as two
 * maps — one for the nav, one for the user menu. It moved here when the side
 * navigation arrived, because the sidebar must gate each entry exactly as
 * the header does; and since #15689 it is ONE map over the one entry
 * vocabulary, so an entry is gated identically wherever it is placed — the
 * sidebar's Main or Bottom list, the header, or a user menu section. The
 * resolvers in `@/lib/org-nav/resolve` then intersect the answer with the
 * org's saved layouts. Listing never grants access: an entry renders only
 * when listed AND available.
 *
 * CLIENT-SAFE: no React, no server reads. The caller resolves `canSee` from
 * the viewer's onboarding sections.
 */

import type { OrgRole } from '@prisma/client-cadence'

import type { ViewerEntryAvailability } from '@/lib/org-nav/resolve'
import { isAdminRole } from '@/lib/role-utils'

export interface ChromeGateInputs {
  /** Issue #2507 — whether an onboarding section is visible to the viewer. */
  canSee: (section: string) => boolean
  /** Issue #3916 — onboarding complete (no section gating). */
  isOnboardingComplete: boolean
  /** Issue #4060/#4150 — the command-bar section, or the training param. */
  commandBarAllowed: boolean
  canAccessSurveys: boolean
  canAccessBoards: boolean
  canAccessPerformance: boolean
  canAccessMeetings: boolean
  canAccessActionBoard: boolean
  canAccessHome: boolean
  canAccessStrategicPlan: boolean
  canAccessAnnouncements: boolean
  /** Issue #15689 — `/templates` admits the viewer (`canViewTemplates`). */
  canAccessTemplates: boolean
  showDemoMode: boolean
  isInDemoMode: boolean
  role: OrgRole | null | undefined
}

/**
 * Issue #16756 — the Performance entry's gate (and `/performance`'s): the
 * page permission (admins, managers, team leads), or — while
 * `performance_teacher_evaluation` is on — any viewer the teacher-evaluation
 * overview shows at least one teacher, so an evaluator who manages nobody
 * can reach their teachers.
 */
export function performanceEntryAllowed(input: {
  pagePermission: boolean
  evaluationFlag: boolean
  seesEvaluationTeacher: boolean
}): boolean {
  return (
    input.pagePermission ||
    (input.evaluationFlag && input.seesEvaluationTeacher)
  )
}

/** Issue #15689 — every entry's gate, wherever the entry is placed. */
export function buildEntryAvailability(
  g: ChromeGateInputs,
): ViewerEntryAvailability {
  return {
    today: true,
    // #15309 / #16727 — where the org's saved navigation lists Home
    // (`orgNavShowsHome`, resolved in `resolveAppChrome`)
    home: g.canAccessHome,
    plan: g.canAccessStrategicPlan,
    actionboard: g.canAccessActionBoard,
    notes: true, // #15677 — notes are unconditional
    // The nav's `pulse` and the user menu's `pulse-check` had this one gate.
    pulse: g.canSee('pulse'),
    orgs: g.canSee('work'), // #16722 — the Handbook is standard
    priorities: true, // #16722 — Priorities are standard
    people: g.canSee('people'),
    meetings: g.canAccessMeetings && g.isOnboardingComplete,
    performance: g.canAccessPerformance && g.isOnboardingComplete,
    plans: g.canSee('plans'),
    boards: g.canAccessBoards && g.canSee('boards'),
    search: g.commandBarAllowed,
    notifications: g.canSee('notifications'),
    feedback: g.canSee('feedback'),
    'ai-chat': true, // #16724 — AI chat is core
    'command-bar': g.commandBarAllowed,
    'company-info': g.canSee('company-info'),
    'presentation-mode': g.isOnboardingComplete,
    'copy-page-link': g.isOnboardingComplete,
    'keyboard-shortcuts': g.isOnboardingComplete,
    training: true,
    'whats-new': g.canAccessAnnouncements,
    surveys: g.canAccessSurveys && isAdminRole(g.role),
    'ai-integrations': g.isOnboardingComplete,
    'demo-mode': g.isInDemoMode || g.showDemoMode,
    templates: g.canAccessTemplates,
  }
}
